import { Converter, productionEnvName, getClientPortalBaseUrls } from '@itm/shared-frontend/lib/utils';

import getEnvVariable from './getEnvVariable';

import {
  TargetPlatform,
  AltNameType,
  PDPStatus,
  MatchingPolicyType,
  MatchingPolicyStatus,
  MatchingPolicyApprovalStatus,
  SearchMatchingStatus,
  SearchPensionValue,
  DataUploadStatusDto,
  DataUploadPdpStatus,
  SelectOption,
  WarningCodeEnum,
  UnavailCodeEnum,
  BenefitTypeEnum,
  AmountTypeEnum,
  CalcType,
  EriTypeEnum,
  DataUploadValidationResult,
  MatchResolutionType,
  ViewDataType,
  TriggerType,
  SlaStatus,
  MissingViewDataStatus,
  ReportType,
  AltusSyncStatus,
  AltusSyncType,
  AltusBatchType,
  PDPRegulatorType,
  PDPRegistrationStatus,
  EnvironmentPublishStatus,
  EnvironmentPublishArea,
  AdditionalSchemeType,
  MatchingOverrideStatus,
  MembershipAuditType,
  PDPSyncType,
  PDPSyncStatus,
  GetPDPRegistrationResponseStatus,
  PDPConnectionAuditArea,
  PDPConnectionAuditAction,
  ExtractLogsType,
  ResyncActionBatchStatus,
  ResyncActionType,
} from 'src/types';

export const ENV_PREFIX = 'REACT_APP_' as const;

export const ENVIRONMENT = getEnvVariable('ENVIRONMENT');
export const TARGET_PLATFORM = getEnvVariable('TARGET_PLATFORM');
export const AUTH_API_URL = getEnvVariable('AUTH_API_URL');
export const ISP_API_URL = getEnvVariable('ISP_API_URL');
export const CLIENT_PORTAL_API_URL = getEnvVariable('CLIENT_PORTAL_API_URL');

export const isProductionEnv = ENVIRONMENT === productionEnvName;
export const isAzureTargetPlatform = TARGET_PLATFORM === TargetPlatform.AZURE;

export const { clientPortalDomain, clientPortalLoginUrl, clientPortalDashboardUrl } =
  getClientPortalBaseUrls(ENVIRONMENT);

export const dataExtractAuditMaxDownloadFileSizeInBytes = 50 * 1024 * 1024; // 50MB
export const convertedDataExtractAuditMaxDownloadFileSize = Converter.formatBytes(
  dataExtractAuditMaxDownloadFileSizeInBytes,
);

export const membershipAltNameTypeOptions: SelectOption[] = Converter.enumToSelectOptions(AltNameType);
export const membershipPDPStatusOptions: SelectOption[] = Converter.enumToSelectOptions(PDPStatus);
export const dataUploadStatusOptions: SelectOption[] = [
  { value: DataUploadStatusDto.InProgress, label: 'In progress' },
  { value: DataUploadStatusDto.UploadFailed, label: 'Failed' },
  { value: DataUploadStatusDto.PartiallyUploaded, label: 'Partially processed' },
  { value: DataUploadStatusDto.UploadSuccessful, label: 'Processed successfully' },
];
export const dataUploadPdpStatusOptions: SelectOption[] = [
  { value: DataUploadPdpStatus.Disabled, label: 'PDP not enabled' },
  { value: DataUploadPdpStatus.NoData, label: 'No data' },
  { value: DataUploadPdpStatus.NotStarted, label: 'Not started' },
  { value: DataUploadPdpStatus.InProgress, label: 'In progress' },
  { value: DataUploadPdpStatus.Success, label: 'Successful' },
  { value: DataUploadPdpStatus.Failed, label: 'Failed' },
  { value: DataUploadPdpStatus.PartiallySuccessful, label: 'Partially successful' },
];
export const matchingPolicyTypeOptions: SelectOption[] = Converter.enumToSelectOptions(MatchingPolicyType);
export const matchingPolicyStatusOptions: SelectOption[] = Converter.enumToSelectOptions(MatchingPolicyStatus);
export const matchingPolicyApprovalStatusOptions: SelectOption[] =
  Converter.enumToSelectOptions(MatchingPolicyApprovalStatus);
export const searchMatchingStatusOptions: SelectOption[] = Converter.enumToSelectOptions(SearchMatchingStatus);
export const searchPensionValueOptions: SelectOption[] = Converter.enumToSelectOptions(SearchPensionValue);
export const membershipPensionsWarningCodeOptions: SelectOption[] = Converter.enumToSelectOptions(WarningCodeEnum);
export const membershipPensionsUnavailCodeOptions: SelectOption[] = Converter.enumToSelectOptions(UnavailCodeEnum);
export const benefitTypeOptions: SelectOption[] = Converter.enumToSelectOptions(BenefitTypeEnum);
export const amountTypeOptions: SelectOption[] = Converter.enumToSelectOptions(AmountTypeEnum);
export const calcTypeOptions: SelectOption[] = Converter.enumToSelectOptions(CalcType);
export const eriTypeOptions: SelectOption[] = Converter.enumToSelectOptions(EriTypeEnum);
export const dataUploadValidationResultOptions: SelectOption[] =
  Converter.enumToSelectOptions(DataUploadValidationResult);
export const possibleMatchesResolutionTypeOptions: SelectOption[] = Converter.enumToSelectOptions(MatchResolutionType);
export const missingViewValuesViewDataTypeOptions: SelectOption[] = Converter.enumToSelectOptions(ViewDataType);
export const missingViewValuesSlaStatusOptions: SelectOption[] = Converter.enumToSelectOptions(SlaStatus);
export const missingViewDataStatusOptions: SelectOption[] = Converter.enumToSelectOptions(MissingViewDataStatus);
export const pdpRegulatorTypeOptions: SelectOption[] = Converter.enumToSelectOptions(PDPRegulatorType);
export const pdpRegistrationStatusOptions: SelectOption[] = Converter.enumToSelectOptions(PDPRegistrationStatus);
export const environmentPublishAreaOptions: SelectOption[] = Converter.enumToSelectOptions(EnvironmentPublishArea);
export const matchingOverrideStatusOptions: SelectOption[] = Converter.enumToSelectOptions(MatchingOverrideStatus);
export const pdpSyncTypeOptions: SelectOption[] = Converter.enumToSelectOptions(PDPSyncType).map(
  ({ value, label }) => ({
    value,
    label: label.replace('resync', 're-sync'),
  }),
);
export const pdpSyncStatusOptions: SelectOption[] = Converter.enumToSelectOptions(PDPSyncStatus);
export const altusSyncTypeOptions: SelectOption[] = Converter.enumToSelectOptions(AltusSyncType);
export const altusBatchTypeOptions: SelectOption[] = Converter.enumToSelectOptions(AltusBatchType);
export const pdpRegistrationResponseStatusOptions: SelectOption[] = Converter.enumToSelectOptions(
  GetPDPRegistrationResponseStatus,
);
export const pdpConnectionAuditAreaOptions: SelectOption[] = Converter.enumToSelectOptions(PDPConnectionAuditArea);
export const extractLogsTypeOptions: SelectOption[] = Converter.enumToSelectOptions(ExtractLogsType);
export const resyncActionBatchStatusOptions: SelectOption[] = Converter.enumToSelectOptions(ResyncActionBatchStatus);

export const pdpConnectionAuditActionOptions: SelectOption[] = Converter.enumToSelectOptions(
  PDPConnectionAuditAction,
).map(({ value, label }) => ({ value, label: label.replace('re run', 're-run').replace('Resync', 'Re-sync') }));

export const manualMatchingDetailsAuditMethodString = 'Manual - Matching details';
export const membershipAuditMethodOptions = [
  'Bulk upload',
  'Manual - Personal details',
  'Manual - ERI details',
  'Manual - Accrued benefit details',
  'Manual - PDP status',
  'PDP - Matching details',
  manualMatchingDetailsAuditMethodString,
  'Manual - Matching override',
].map<SelectOption>((v) => ({ value: v, label: v }));

export const searchPDPStatusOptions = [
  { label: 'Active', value: PDPStatus.Active },
  { label: 'Excluded (NEW)', value: PDPStatus.NEW },
  { label: 'Excluded (CALL)', value: PDPStatus.CALL },
];

export const memberExtractReportTypeOptions: SelectOption[] = [
  { label: 'Full scheme', value: ReportType.FullScheme },
  { label: 'Mi data (find and views)', value: ReportType.MiData },
  {
    label: 'View data SLA extract (missing pension, unavailable code and out of date values)',
    value: ReportType.MissingPensionValueData,
  },
  { label: 'Matching results', value: ReportType.MatchingResults },
  { label: 'Membership extract (personal, pension and values)', value: ReportType.MembershipData },
  { label: 'Digitally verified personal data (full matches only)', value: ReportType.DigitallyVerifiedPersonalData },
  { label: 'Fusion data changes extract', value: ReportType.MembershipDetails },
];

export const altusSyncStatusOptions: SelectOption[] = [
  { value: AltusSyncStatus.NotStarted, label: 'Not started' },
  { value: AltusSyncStatus.PendingProcessing, label: 'Queued' },
  { value: AltusSyncStatus.Processing, label: 'In progress' },
  { value: AltusSyncStatus.Processed, label: 'Success' },
  { value: AltusSyncStatus.Failed, label: AltusSyncStatus.Failed },
  { value: AltusSyncStatus.Aborted, label: AltusSyncStatus.Aborted },
  { value: AltusSyncStatus.ProcessedWithErrors, label: 'Processed with errors' },
];

export const altusSyncStatusWithMembershipAuditTypeOptions = [
  ...altusSyncStatusOptions,
  { value: MembershipAuditType.NotAvailable, label: 'Not available' },
].toSpliced(1, 0, { value: MembershipAuditType.DirectUpdate, label: 'Direct update' });

export const environmentPublishStatusOptions: SelectOption[] = [
  { value: EnvironmentPublishStatus.Success, label: 'Published successfully' },
  { value: EnvironmentPublishStatus.Failed, label: 'Publish failed' },
];

export const additionalSchemeTypeOptions: SelectOption[] = [
  { value: AdditionalSchemeType.FOR, label: 'Former' },
  { value: AdditionalSchemeType.OTH, label: 'Other' },
];

export const resyncActionTypeOptions: SelectOption[] = [
  { value: ResyncActionType.FullSchemeResync, label: 'Full scheme data re-sync' },
  { value: ResyncActionType.MissingViewComparison, label: 'Missing view data comparison' },
  { value: ResyncActionType.UpdateClientConfiguration, label: 'Re-sync client configuration' },
  { value: ResyncActionType.DeregisteredMembershipMatchingSync, label: 'Re-sync matching with Equisoft' },
];

export const missingViewValuesTriggerTypeOptions: SelectOption[] = [
  { value: TriggerType.Matching, label: 'Full Match' },
  { value: TriggerType.ViewData, label: 'View Request' },
];

export const booleanOptions: SelectOption[] = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
];

export const altusDeleteSuccessOptions: SelectOption[] = [
  { value: 'true', label: 'Success' },
  { value: 'false', label: 'Failed' },
];

export const bankHolidayYearOptions: SelectOption[] = ['2025', '2026', '2027', '2028'].map((year) => ({
  value: year,
  label: year,
}));

export const phoneRegExp = /^(?=.{1,20}$)(?:[+]?[\s0-9]*[(]{0,1}[0-9]{1,4}[)]{0,1})?[-\s0-9]*$/;
